"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon, CalendarDaysIcon, UserCircleIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";

function formatDate(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Blogs() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch("https://api.insightos.co.uk/api/productupdates", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((r) => r.json())
      .then((data) => {
                console.log("Raw API response:", data);

        const items = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        const blogs = items.filter((p) => p.Blog === 1 && p.Live !== 0);
          console.log("Fetched blogs:", blogs);
        setPosts(blogs);
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-10 w-10 rounded-full border-4 border-indigo-300 border-t-indigo-600 animate-spin" />
      </div>
    );
  }

  if (!posts.length) return null;

  return (
    <section className="py-24 bg-gradient-to-b from-white via-indigo-50/60 to-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="mb-12 text-center"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 mb-4">
            <SparklesIcon className="h-3.5 w-3.5 text-indigo-500" />
            Product Updates
          </span>
          <h2 className="text-3xl font-black tracking-tight text-gray-900 sm:text-4xl">
            Latest from{" "}
            <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Avenor AI & InsightOS
            </span>
          </h2>
          <p className="mt-3 text-base text-gray-500 max-w-xl mx-auto">
            New features, improvements and stories from the team.
          </p>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
          {posts.map((post, i) => (
            <motion.article
              key={post.Title + i}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.07 * i, duration: 0.5, ease: "easeOut" }}
              onClick={() => setSelected(post)}
              className="group relative flex flex-col overflow-hidden rounded-3xl border border-white/60 bg-white shadow-[0_16px_48px_rgba(99,102,241,0.12)] cursor-pointer transition-all duration-300 hover:shadow-[0_24px_60px_rgba(99,102,241,0.22)] hover:-translate-y-1"
            >
              {/* Gradient border accent */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-400/10 via-purple-400/5 to-cyan-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

              {/* Image */}
              <div className="relative h-52 sm:h-56 w-full overflow-hidden bg-gradient-to-br from-indigo-100 via-purple-50 to-sky-100 flex-shrink-0">
                {post.ImageUrl ? (
                  <img
                    src={post.ImageUrl}
                    alt={post.Title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <SparklesIcon className="h-12 w-12 text-indigo-300" />
                  </div>
                )}
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              </div>

              {/* Content */}
              <div className="flex flex-1 flex-col p-5">
                {/* Meta */}
                <div className="flex items-center gap-3 mb-3">
                  {post.Author && (
                    <span className="flex items-center gap-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full">
                      <UserCircleIcon className="h-3.5 w-3.5" />
                      {post.Author}
                    </span>
                  )}
                  {post.CreatedAt && (
                    <span className="flex items-center gap-1 text-[11px] text-gray-400">
                      <CalendarDaysIcon className="h-3.5 w-3.5" />
                      {formatDate(post.CreatedAt)}
                    </span>
                  )}
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-indigo-700 transition-colors">
                  {post.Title}
                </h3>

                <p className="text-sm text-gray-500 line-clamp-3 flex-1 leading-relaxed">
                  {post.Content?.replace(/<[^>]*>/g, "").slice(0, 200)}
                  {post.Content?.length > 200 ? "…" : ""}
                </p>

                <div className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-indigo-600">
                  Read more
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* ── Full-post modal ── */}
      <AnimatePresence>
        {selected && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setSelected(null)}
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            />

            {/* Modal sheet */}
            <motion.div
              key="modal"
              initial={{ opacity: 0, y: 60, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="fixed inset-x-4 bottom-0 top-6 z-50 mx-auto flex max-w-3xl flex-col overflow-hidden rounded-t-3xl sm:rounded-3xl sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:top-8 sm:bottom-8 sm:w-full"
              style={{ maxHeight: "calc(100vh - 3rem)" }}
            >
              {/* Gradient border ring */}
              <div className="absolute -inset-[2px] rounded-3xl bg-gradient-to-br from-indigo-400 via-purple-400 to-cyan-400 opacity-60 -z-10" />

              <div className="relative flex flex-col h-full overflow-hidden rounded-3xl bg-white shadow-2xl">
                {/* Header image */}
                {selected.ImageUrl && (
                  <div className="relative h-48 sm:h-64 w-full flex-shrink-0 overflow-hidden bg-gradient-to-br from-indigo-100 via-purple-50 to-sky-100">
                    <img
                      src={selected.ImageUrl}
                      alt={selected.Title}
                      className="h-full w-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                    {/* Close button on image */}
                    <button
                      onClick={() => setSelected(null)}
                      className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}

                {/* Top bar (when no image) */}
                {!selected.ImageUrl && (
                  <div className="flex items-center justify-between bg-gradient-to-r from-indigo-900 via-purple-800 to-indigo-700 px-5 py-4 flex-shrink-0">
                    <span className="text-sm font-semibold text-white/80">Product Update</span>
                    <button
                      onClick={() => setSelected(null)}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6">
                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {selected.Author && (
                      <span className="flex items-center gap-1 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded-full">
                        <UserCircleIcon className="h-4 w-4" />
                        {selected.Author}
                      </span>
                    )}
                    {selected.CreatedAt && (
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <CalendarDaysIcon className="h-4 w-4" />
                        {formatDate(selected.CreatedAt)}
                      </span>
                    )}
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-4 leading-tight">
                    {selected.Title}
                  </h2>

                  {/* Content — render HTML if present, otherwise plain text */}
                  {selected.Content?.trim().startsWith("<") ? (
                    <div
                      className="prose prose-indigo prose-sm sm:prose-base max-w-none text-gray-700"
                      dangerouslySetInnerHTML={{ __html: selected.Content }}
                    />
                  ) : (
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
                      {selected.Content}
                    </p>
                  )}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 flex items-center justify-between border-t border-indigo-100/60 bg-gradient-to-r from-indigo-50/80 via-white to-purple-50/80 px-5 py-3 sm:px-8">
                  <span className="text-xs text-gray-400">Avenor AI & InsightOS Product Update</span>
                  <button
                    onClick={() => setSelected(null)}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50 transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
